import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotifyService } from 'src/app/services/notify.service';
import { ValueService } from 'src/app/services/value.service';
import { MatDialog } from '@angular/material/dialog';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { ApiService } from 'src/app/services/api.service';
import { SuccessExcelModalComponent } from './success-excel-modal/success-excel-modal.component';
import { Router } from '@angular/router';
/**
 * Validator function to validate multiple email addresses.
 * @returns A ValidatorFn that checks for the validity of email addresses.
 */
export function multipleEmailsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // No value means no error
    if (!control.value) {
      return null;
    }

    // Split the input string into individual email addresses
    const emails = control.value.split(',').map((email) => email.trim());

    // Regular expression to validate email addresses
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Filter out invalid email addresses
    const invalidEmails = emails.filter((email) => !emailPattern.test(email));

    // Return an error object with the invalid emails, if any
    return invalidEmails.length > 0
      ? { invalidEmails: invalidEmails.join(', ') }
      : null;
  };
}
@Component({
  selector: 'app-email-modal',
  templateUrl: './email-modal.component.html',
  styleUrls: ['./email-modal.component.css'],
})
export class EmailModalComponent implements OnInit {
  emailForm: FormGroup;
  datas;
  private listApi = environment.url + environment.preincubaterList;
  private listApis = environment.url + environment.CoeList;
  /**
   * Constructs a new instance of the EmailModalComponent.
   * @param fb The FormBuilder instance to create form controls.
   * @param router The Router for navigation.
   * @param getdata The ValueService to manage application values.
   * @param makeapi The ApiService to handle API requests.
   * @param notify The NotifyService to handle notifications.
   * @param data The data that has been passed from the parent component.
   * @param dialogRef The MatDialogRef to manage the dialog reference.
   * @param dialog The MatDialog to manage dialog operations.
   */
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private getdata: ValueService,
    private makeapi: ApiService,
    private notify: NotifyService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<EmailModalComponent>,
    public dialog: MatDialog
  ) {
    /**
     * The data that has been passed from the parent component.
     */
    this.datas = data;
    /**
     * The form group for the email form.
     * Contains the email field, which is required and must be a valid email format.
     */
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, multipleEmailsValidator()]],
    });
  }
  get add() {
    return this.emailForm.controls;
  }
  ngOnInit(): void {}
  /**
   * Saves the email form data and sends an email with the list of incubators/coes based on the selected criteria.
   * The method first checks if the form is valid and if not, shows an error message.
   * If the form is valid, it shows a loading indicator and closes the dialog.
   * It then creates the request object with the search criteria and the email addresses.
   * The method then makes a POST request to the API endpoint to send the email.
   * If the response is successful, it shows a success message and resets the form.
   * If the response is not successful, it shows an error message.
   */
  ExcelSave() {
    let getform = this.emailForm.value;
    if (this.emailForm.invalid) {
      // If the form is invalid, mark all fields as touched and show an error message
      this.emailForm.markAllAsTouched();
      this.notify.notifyError('Please fill the mandatory fields !!');
      return;
    } else {
      // If the form is valid, show a loading indicator and close the dialog
      this.getdata.amizhthLoader('show');
      this.dialogRef.close();
      // Set the search string to an empty string if it is undefined
      if (this.datas._key.searchString == undefined) {
        this.datas._key.searchString = '';
      }
      // Determine if the route is for coe list or incubator list
      const isCoeRoute = this.router.url.includes('coe-list');
      // Select the appropriate API endpoint
      const selectedApi = isCoeRoute ? this.listApis : this.listApi;
      // Create the request object
      let req = {
        listSize: this.datas._key.listSize,
        pageNumber: this.datas._key.pageNumber,
        searchString: this.datas._key.searchString,
        status: this.datas._key.status,
        email: getform.email,
      };
      // Make the API call to send the email
      return this.makeapi.method(selectedApi, req, 'post').subscribe((data) => {
        // Hide the loading indicator
        this.getdata.amizhthLoader('hide');
        // Check if the response is successful
        if (data.responseStatus == 'Success') {
          // If the response is successful, show a success message and reset the form
          this.notify.notifysuccess(data.responseMessage);
          this.emailForm.reset();
          this.dialogRef.close();
          // Open the success dialog
          this.openModal();
        } else {
          // If the response is not successful, show an error message
          this.notify.notifyError(data.responseMessage);
        }
      });
    }
  }
  dialogRefs;
  /**
   * Opens a modal dialog for displaying a success message when an email is sent with the list of incubators.
   * The modal dialog is opened with a width of 600px and a height of 360px.
   */
  openModal() {
    this.dialogRefs = this.dialog.open(SuccessExcelModalComponent, {
      width: '600px',
      height: '360px',
    });

    /**
     * Subscribes to the afterClosed event of the modal dialog and logs the result.
     * This is just for debugging purposes.
     */
    this.dialogRefs.afterClosed().subscribe((result) => {
      console.log('The dialog was closed', result);
    });
  }
  /**
   * Cancels the email modal and resets the form.
   */
  cancel(): void {
    // Reset the form fields
    this.emailForm.reset();
    // Close the dialog
    this.dialogRef.close();
  }
}
